import { motion } from "framer-motion";
import { MusicNotesSimple, Microphone, Heart, DeviceMobile, Sparkle } from "@phosphor-icons/react";
import { useLang } from "@/context/LanguageContext";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
};

const word = {
  hidden: { opacity: 0 },
  show: (i) => ({
    opacity: 1,
    transition: { duration: 0.4, delay: 0.15 + i * 0.05 },
  }),
};

const FEATURE_ICONS = [MusicNotesSimple, Microphone, Heart, DeviceMobile];

export default function Hero() {
  const { t } = useLang();
  const title1 = t("hero.title1").split(" ");
  const title2 = t("hero.title2").split(" ");
  const features = t("hero.features");
  return (
    <section className="relative pt-32 pb-16 sm:pt-40 sm:pb-24 overflow-hidden">
      <div className="blob w-[420px] h-[420px] -top-20 -left-24" style={{ background: "hsl(351 100% 82%)" }} />
      <div className="blob w-[380px] h-[380px] top-40 -right-20" style={{ background: "hsl(200 80% 80%)" }} />
      <div className="blob w-[300px] h-[300px] bottom-0 left-1/3" style={{ background: "hsl(298 60% 82%)" }} />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 flex flex-col items-start text-left">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="inline-flex items-center gap-2 rounded-full glass border border-white/60 px-4 py-2 mb-7 shadow-glass"
        >
          <Sparkle weight="fill" className="text-primary" size={18} />
          <span className="text-sm font-bold text-foreground/70">{t("hero.badge")}</span>
        </motion.div>

        <h1 className="font-display font-bold leading-[1.05] text-[2rem] sm:text-4xl lg:text-5xl tracking-tight text-foreground">
          <span className="block py-1 lg:whitespace-nowrap">
            {title1.map((w, wi) => (
              <motion.span key={wi} custom={wi} variants={word} initial="hidden" animate="show" className="inline-block mr-2.5">
                {w}
              </motion.span>
            ))}
          </span>
          <span className="block py-1 lg:whitespace-nowrap">
            {title2.map((w, wi) => (
              <motion.span key={wi} custom={title1.length + wi} variants={word} initial="hidden" animate="show" className="inline-block mr-2.5 text-primary">
                {w}
              </motion.span>
            ))}
            <motion.span
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.9, type: "spring", stiffness: 200 }}
              className="inline-block"
            >
              ❤️
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9 }}
          className="mt-6 text-base sm:text-lg text-foreground/60 max-w-2xl font-medium"
        >
          {t("hero.desc")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.0 }}
          className="mt-8 flex flex-wrap gap-3"
        >
          {features.map((f, i) => {
            const Icon = FEATURE_ICONS[i] || MusicNotesSimple;
            return (
              <span
                key={i}
                data-testid={`hero-feature-${i}`}
                className="flex items-center gap-2 rounded-full glass border border-white/60 px-4 py-2 text-sm font-semibold text-foreground/75 shadow-glass"
              >
                <Icon weight="duotone" className="text-primary" size={18} />
                {f}
              </span>
            );
          })}
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.15 }}
          className="mt-8 font-display font-bold text-2xl sm:text-3xl text-foreground"
        >
          {t("hero.price")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.25 }}
          className="mt-6 flex flex-col items-start gap-4"
        >
          <motion.button
            data-testid="hero-cta-crear"
            whileHover={{ scale: 1.05, rotate: -1 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => scrollTo("crear")}
            className="rounded-full bg-primary px-9 py-4 text-base font-bold text-primary-foreground shadow-soft-lg"
          >
            {t("hero.cta1")}
          </motion.button>

          <div className="mt-2 flex flex-col items-start gap-2">
            <span className="text-sm font-medium text-foreground/55">{t("hero.listenQ")}</span>
            <motion.button
              data-testid="hero-cta-ejemplos"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => scrollTo("ejemplos")}
              className="rounded-full glass border border-white/60 px-7 py-3 text-sm font-bold text-foreground/80 shadow-glass"
            >
              🎧 {t("hero.listenCta")}
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
